import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { ProfileFields } from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { UserAcademicHistoryRequest } from '@backrs_ui/grow-ui/src/redux/userPortfolio/userAcademicHistory.api';
import { UserEmploymentHistoryRequest } from '@backrs_ui/grow-ui/src/redux/userPortfolio/userEmploymentHistory.api';
import { UserEngagementHistoryRequest } from '@backrs_ui/grow-ui/src/redux/userPortfolio/userEngagementHistory.api';

import {
  formatGPA,
  formatLettersOnly,
  formatMMYYYY,
  formatStateAbbreviation,
} from './question.helpers';

export type ResumeQuestionField = Readonly<{
  key: ProfileFields | string;
  label: string | undefined;
  getLabel?: (formValues: Record<string, (string | number | object)[]>) => string;
  required?: boolean;
  type?: 'profile' | 'small_freeform' | 'yes_no' | 'large_freeform' | 'mm/yyyy';
  placeholder?: string;
  helperText?: string;
  academicHistoryKey?: keyof UserAcademicHistoryRequest;
  employmentHistoryKey?: keyof UserEmploymentHistoryRequest;
  engagementHistoryKey?: keyof UserEngagementHistoryRequest;
  maxSelectedOptions?: number;
  formatValue?: (value: string) => string;
  validation?: (value: string) => string;
}>;

const WORK_EXPERIENCE_STRINGS = appStrings.modules.solutionSessions.questions.workExperience.fields;
const EDUCATION_STRINGS = appStrings.modules.solutionSessions.questions.education.fields;
const ENGAGEMENT_STRINGS = appStrings.modules.solutionSessions.questions.engagement.fields;

export const EDUCATION_FIELDS: ResumeQuestionField[] = [
  {
    key: 'academicsHighSchoolCurrentEnrollment',
    label: EDUCATION_STRINGS.institutionName.label,
    required: true,
    type: 'profile',
    academicHistoryKey: 'institution_name',
  },
  {
    key: 'academicsPostSecondaryCurrentEnrollment',
    label: EDUCATION_STRINGS.institutionName.label,
    required: true,
    type: 'profile',
    academicHistoryKey: 'institution_name',
  },
  {
    key: 'institution_city',
    label: EDUCATION_STRINGS.institutionCity.label,
    required: true,
    type: 'small_freeform',
    placeholder: EDUCATION_STRINGS.institutionCity.placeholder,
    academicHistoryKey: 'institution_city',
    formatValue: formatLettersOnly,
  },
  {
    key: 'institution_state',
    label: EDUCATION_STRINGS.institutionState.label,
    required: true,
    type: 'small_freeform',
    placeholder: EDUCATION_STRINGS.institutionState.placeholder,
    academicHistoryKey: 'institution_state',
    formatValue: formatStateAbbreviation,
  },
  {
    key: 'academicDegrees',
    label: EDUCATION_STRINGS.academicDegrees.label,
    type: 'profile',
    academicHistoryKey: 'degree_id',
  },
  {
    key: 'academicMajors',
    label: EDUCATION_STRINGS.academicMajors.label,
    type: 'profile',
    academicHistoryKey: 'majors',
  },
  {
    key: 'did_graduate',
    label: EDUCATION_STRINGS.didGraduate.label,
    required: true,
    type: 'yes_no',
    academicHistoryKey: 'did_graduate',
  },
  {
    key: 'graduation_date',
    label: undefined,
    getLabel: (formValues) => {
      const didGraduate = formValues['did_graduate']?.[0];

      return didGraduate === 'Yes'
        ? EDUCATION_STRINGS.graduationDate.labelGraduated
        : EDUCATION_STRINGS.graduationDate.labelExpectedGraduation;
    },
    type: 'mm/yyyy',
    placeholder: 'MM/YYYY',
    academicHistoryKey: 'graduation_date',
    formatValue: formatMMYYYY,
  },
  {
    key: 'gpa',
    label: EDUCATION_STRINGS.highSchoolGpa.label,
    type: 'small_freeform',
    academicHistoryKey: 'gpa',
    placeholder: '4.00',
    formatValue: formatGPA,
    validation: (value: string) => {
      if (Number(value) > 4.0) return 'GPA must be less than or equal to 4.0';
      if (Number(value) < 0) return 'GPA must be greater than 0';

      return '';
    },
  },
  {
    key: 'major_gpa',
    label: EDUCATION_STRINGS.majorGpa.label,
    type: 'small_freeform',
    academicHistoryKey: 'major_gpa',
    placeholder: '4.00',
    formatValue: formatGPA,
    validation: (value: string) => {
      if (Number(value) > 4.0) return 'GPA must be less than or equal to 4.0';
      if (Number(value) < 0) return 'GPA must be greater than 0';

      return '';
    },
  },
  {
    key: 'awards_accomplishments',
    label: EDUCATION_STRINGS.awardsAccomplishments.label,
    type: 'large_freeform',
    placeholder: EDUCATION_STRINGS.awardsAccomplishments.placeholder,
    academicHistoryKey: 'awards_accomplishments',
  },
];

export const WORK_EXPERIENCE_FIELDS: ResumeQuestionField[] = [
  {
    key: 'careerEmployer',
    label: WORK_EXPERIENCE_STRINGS.employerName.label,
    required: true,
    type: 'profile',
    employmentHistoryKey: 'employer_name',
    maxSelectedOptions: 1,
  },
  {
    key: 'job_title',
    label: WORK_EXPERIENCE_STRINGS.jobTitle.label,
    required: true,
    type: 'small_freeform',
    employmentHistoryKey: 'job_title',
    placeholder: WORK_EXPERIENCE_STRINGS.jobTitle.placeholder,
    formatValue: formatLettersOnly,
  },
  {
    key: 'employer_city',
    label: WORK_EXPERIENCE_STRINGS.employerCity.label,
    required: true,
    type: 'small_freeform',
    placeholder: WORK_EXPERIENCE_STRINGS.employerCity.placeholder,
    employmentHistoryKey: 'employer_city',
    formatValue: formatLettersOnly,
  },
  {
    key: 'employer_state',
    label: WORK_EXPERIENCE_STRINGS.employerState.label,
    required: true,
    type: 'small_freeform',
    placeholder: WORK_EXPERIENCE_STRINGS.employerState.placeholder,
    employmentHistoryKey: 'employer_state',
    formatValue: formatStateAbbreviation,
  },
  {
    key: 'start_date',
    label: WORK_EXPERIENCE_STRINGS.startDate.label,
    required: true,
    type: 'mm/yyyy',
    employmentHistoryKey: 'start_date',
    placeholder: WORK_EXPERIENCE_STRINGS.startDate.placeholder,
    formatValue: formatMMYYYY,
  },
  {
    key: 'end_date',
    label: WORK_EXPERIENCE_STRINGS.endDate.label,
    type: 'mm/yyyy',
    employmentHistoryKey: 'end_date',
    placeholder: WORK_EXPERIENCE_STRINGS.endDate.placeholder,
    helperText: WORK_EXPERIENCE_STRINGS.endDate.helperText,
    formatValue: formatMMYYYY,
  },
  {
    key: 'achievements',
    label: WORK_EXPERIENCE_STRINGS.achievements.label,
    type: 'large_freeform',
    employmentHistoryKey: 'achievements',
    placeholder: WORK_EXPERIENCE_STRINGS.achievements.placeholder,
  },
];

export const ENGAGEMENT_FIELDS: ResumeQuestionField[] = [
  {
    key: 'organizationName',
    label: ENGAGEMENT_STRINGS.organizationName.label,
    required: true,
    type: 'small_freeform',
    engagementHistoryKey: 'organization_name',
    placeholder: ENGAGEMENT_STRINGS.organizationName.placeholder,
    maxSelectedOptions: 1,
  },
  {
    key: 'role',
    label: ENGAGEMENT_STRINGS.role.label,
    required: true,
    type: 'small_freeform',
    engagementHistoryKey: 'role',
    placeholder: ENGAGEMENT_STRINGS.role.placeholder,
    formatValue: formatLettersOnly,
  },
  {
    key: 'city',
    label: ENGAGEMENT_STRINGS.city.label,
    required: true,
    type: 'small_freeform',
    placeholder: ENGAGEMENT_STRINGS.city.placeholder,
    engagementHistoryKey: 'city',
    formatValue: formatLettersOnly,
  },
  {
    key: 'state',
    label: ENGAGEMENT_STRINGS.state.label,
    required: true,
    type: 'small_freeform',
    placeholder: ENGAGEMENT_STRINGS.state.placeholder,
    engagementHistoryKey: 'state',
    formatValue: formatStateAbbreviation,
  },
  {
    key: 'start_date',
    label: ENGAGEMENT_STRINGS.startDate.label,
    required: true,
    type: 'mm/yyyy',
    engagementHistoryKey: 'start_date',
    placeholder: ENGAGEMENT_STRINGS.startDate.placeholder,
    formatValue: formatMMYYYY,
  },
  {
    key: 'end_date',
    label: ENGAGEMENT_STRINGS.endDate.label,
    type: 'mm/yyyy',
    engagementHistoryKey: 'end_date',
    placeholder: ENGAGEMENT_STRINGS.endDate.placeholder,
    helperText: ENGAGEMENT_STRINGS.endDate.helperText,
    formatValue: formatMMYYYY,
  },
  {
    key: 'achievements',
    label: ENGAGEMENT_STRINGS.achievements.label,
    type: 'large_freeform',
    engagementHistoryKey: 'achievements',
    placeholder: ENGAGEMENT_STRINGS.achievements.placeholder,
  },
];
